import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Lock, Sparkles, Send, RotateCcw, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProviderCard, { LockedProviderCard } from "@/components/ProviderCard";
import CareRequestDialog from "@/components/CareRequestDialog";
import CareMatchForm from "@/components/carematch/CareMatchForm";
import { NotifyOptInDialog } from "@/components/carematch/NotificationSettings";
import { Loader, Empty } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export default function CareResults() {
  const { id } = useParams();
  const { user } = useAuth();
  const [dlg, setDlg] = useState(null);
  const [cm, setCm] = useState(false);
  const [optIn, setOptIn] = useState(false);
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ["search", id, user?.id], queryFn: () => api.get(`/care-search/${id}`).then((r) => r.data), enabled: user !== null });
  if (isLoading || user === null) return <Loader label="Finding your matches" />;
  if (error) return <div className="max-w-3xl mx-auto p-8"><Empty title="We couldn't open this search" body={errMsg(error)} action={<Button asChild className="rounded-full"><Link to="/find-care">Start a new search</Link></Button>} /></div>;

  const accepting = data.results.filter((r) => r.accepting_new_clients && !data.requested_org_ids.includes(r.id));
  const sendAll = async () => {
    try {
      const { data: res } = await api.post("/care-requests", { org_ids: accepting.map((r) => r.id), search_session_id: id, message: "" });
      toast.success(`Care request sent to ${res.created.length} provider(s)`);
      refetch();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="cz-hero-bg min-h-[80vh]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <span className="cz-eyebrow"><Sparkles className="h-3.5 w-3.5" />{data.care_type} · {data.area}</span>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#003B2D] mt-4" data-testid="results-title">
              {data.total ? `We found ${data.total} matching provider${data.total > 1 ? "s" : ""}` : "No exact matches yet"}
            </h1>
            <p className="text-slate-600 mt-2" data-testid="results-accepting-count">{data.accepting_count} currently accepting new clients · sorted with accepting providers first</p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" className="rounded-full bg-white" data-testid="results-new-search-btn"><Link to="/find-care"><RotateCcw className="h-4 w-4 mr-1" />New search</Link></Button>
            {data.unlocked && user?.roles?.includes("consumer") ? (
              <Button variant="outline" className="rounded-full bg-white border-[#D4AF63] text-[#003B2D]" onClick={() => setCm(true)} data-testid="carematch-notify-btn"><BellRing className="h-4 w-4 mr-1 text-[#9a7a2f]" />Notify me when matching care becomes available</Button>
            ) : !user && (
              <Button asChild variant="outline" className="rounded-full bg-white border-[#D4AF63] text-[#003B2D]" data-testid="carematch-guest-notify-btn"><Link to={`/register?role=consumer&search=${id}`}><BellRing className="h-4 w-4 mr-1 text-[#9a7a2f]" />Get CareMatch™ alerts</Link></Button>
            )}
            {data.unlocked && accepting.length > 0 && <Button className="rounded-full zv-btn-gold" onClick={sendAll} data-testid="request-all-accepting-btn"><Send className="h-4 w-4 mr-1" />Request care from all {accepting.length} accepting</Button>}
          </div>
        </div>

        {!data.unlocked && data.total > 0 && (
          <div className="mt-8 rounded-3xl bg-[#003B2D] text-white p-6 sm:p-8 flex flex-col md:flex-row gap-6 md:items-center justify-between relative overflow-hidden" data-testid="locked-matches-overlay">
            <div className="absolute inset-0 cz-passport-lines" />
            <div className="relative flex gap-4 items-start">
              <span className="h-12 w-12 rounded-2xl bg-teal-500/20 grid place-items-center shrink-0"><Lock className="h-5 w-5 text-teal-300" /></span>
              <div>
                <div className="font-display text-xl font-bold">We found providers that match your needs.</div>
                <p className="text-slate-300 mt-1">Create your free Zunvel account to unlock your matches and connect with providers accepting clients. Your answers are saved — no need to start over.</p>
              </div>
            </div>
            <div className="relative flex gap-2 shrink-0">
              <Button asChild className="rounded-full zv-btn-gold h-11 px-6" data-testid="unlock-register-btn"><Link to={`/register?role=consumer&search=${id}`}>Unlock my matches</Link></Button>
              <Button asChild variant="ghost" className="rounded-full text-white hover:bg-white/10 h-11" data-testid="unlock-login-btn"><Link to={`/login?search=${id}`}>Sign in</Link></Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="results-grid">
          {data.results.map((p, i) => data.unlocked
            ? <ProviderCard key={p.id} p={p} requested={data.requested_org_ids.includes(p.id)} onRequest={(prov, mode) => setDlg({ prov, mode })} />
            : <LockedProviderCard key={i} p={p} />)}
        </div>
        {data.total === 0 && <Empty title="No providers match those exact needs yet" body="Zunvel is growing every week. Try a different care type or browse all providers." action={<Button asChild className="rounded-full"><Link to="/providers">Browse providers</Link></Button>} />}
      </div>
      {dlg && <CareRequestDialog provider={dlg.prov} mode={dlg.mode} searchId={id} onClose={() => setDlg(null)} onSent={() => refetch()} />}
      {data.unlocked && <CareMatchForm open={cm} onClose={() => setCm(false)} answers={data.answers} searchId={id} onSaved={(r) => { if (r?.first) setOptIn(true); else toast.info("Manage it any time in My Zunvel"); }} />}
      <NotifyOptInDialog open={optIn} onClose={() => setOptIn(false)} />
    </div>
  );
}
